import { useState } from 'react';
import { useSearch } from '@tanstack/react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { DEFAULT_SETTINGS, formatMoney, hoy, type Centavos, type CuentaBancoCordobas } from '@abastece/shared';
import { db } from '../db/dexie.js';
import { readSettings } from '../db/reader.js';
import { cuentaEnCordobas } from '../lib/bancos.js';
import { emitir } from '../lib/emitir.js';
import { otrosUsos, useUsosDeReferencias, yaPago } from '../lib/referencias.js';
import { usePuede } from '../lib/sesion.js';
import { Campo } from '../ui/Campo.js';
import { ElegirCuenta } from '../ui/ElegirCuenta.js';
import { Modal } from '../ui/Modal.js';
import { Pagina, Vacio } from '../ui/Pagina.js';
import { Ico } from '../ui/Iconos.js';
import { useT } from '../i18n/index.js';

/** One payment waiting to be seen in the bank or the wallet: a sale's, or a top-up's. */
export interface EnCola {
  /** `venta:<id>:<indice>` or `recarga:<id>`, as `lib/referencias.ts` names payments. */
  clave: string;
  fecha: string;
  monto: Centavos;
  forma: string;
  referencia: string | null;
  venta?: { id: string; ticket: string; indice: number; clienteId: string | null };
  recarga?: { id: string; operadora: string; telefono: string };
  /** An abono by transfer or wallet a cashier took (`fiado.ts`): `abono:<id>`. */
  abono?: { id: string; clienteId: string };
}

/** What `pago.confirmado` and `pago.rechazado` name the payment by. */
export const destinoDe = (x: EnCola) =>
  x.venta ? { ventaId: x.venta.id, indice: x.venta.indice } : x.abono ? { abonoId: x.abono.id } : { recargaId: x.recarga!.id };

/** Days since `fecha`, a Nicaraguan calendar date, counted in whole days. */
export const diasDesde = (fecha: string): number =>
  Math.max(0, Math.round((Date.parse(`${hoy()}T12:00:00Z`) - Date.parse(`${fecha}T12:00:00Z`)) / 86_400_000));

/** Everything in the queue, oldest first: the longer one has waited, the likelier it never came. */
export async function pagosEnCola(): Promise<EnCola[]> {
  const [ventas, recargas, abonos] = await Promise.all([
    db.ventas.where('enCola').equals(1).toArray(),
    db.recargas.filter((r) => r.estadoPago === 'pendiente').toArray(),
    db.abonos.filter((a) => a.estadoPago === 'pendiente' && !a.anulado).toArray(),
  ]);
  const cola: EnCola[] = [];
  for (const v of ventas) {
    if (v.anulada) continue;
    v.pagos.forEach((p, indice) => {
      if (p.estado !== 'pendiente') return;
      cola.push({
        clave: `venta:${v.id}:${indice}`,
        fecha: v.fecha,
        monto: p.monto,
        forma: p.forma,
        referencia: p.referencia,
        venta: { id: v.id, ticket: v.ticket, indice, clienteId: v.clienteId },
      });
    });
  }
  for (const r of recargas) {
    cola.push({
      clave: `recarga:${r.id}`,
      fecha: r.fecha,
      monto: r.montoFacial,
      forma: r.formaPago,
      referencia: r.referencia,
      recarga: { id: r.id, operadora: r.operadora, telefono: r.telefono },
    });
  }
  for (const a of abonos) {
    cola.push({
      clave: `abono:${a.id}`,
      fecha: a.fecha,
      monto: a.monto,
      forma: a.formaPago,
      referencia: a.referencia,
      abono: { id: a.id, clienteId: a.clienteId },
    });
  }
  return cola.sort((a, b) => a.fecha.localeCompare(b.fecha) || a.clave.localeCompare(b.clave));
}

/**
 * The unconfirmed-transfer queue, wallet payments and top-ups included.
 *
 * A screenshot is not a payment. Transfers and wallet payments taken at the counter sit in a
 * holding account until somebody checks the bank or the wallet and clears them here — which is
 * about the cheapest fraud control available to a shop that accepts them. Each says how long it
 * has waited and which receipt it came with, and a receipt that paid something else as well says
 * that too.
 */
export function Transferencias() {
  const t = useT();
  const { desde } = useSearch({ from: '/transferencias' });
  const pendientes = useLiveQuery(() => pagosEnCola(), [], []);
  const clientes = useLiveQuery(() => db.clientes.toArray(), [], []);
  const usos = useUsosDeReferencias();
  const puede = usePuede('confirmar_transferencia');
  const [rechazando, setRechazando] = useState<string | null>(null);
  const [motivo, setMotivo] = useState('');
  /** A reference typed at confirmation, for a payment that came without one. */
  const [refs, setRefs] = useState<Record<string, string>>({});
  const config = useLiveQuery(() => readSettings(), [], DEFAULT_SETTINGS);
  /** The córdoba account a transfer was seen in, when the shop has more than one: where transfers go, unless picked. */
  const [cuentas, setCuentas] = useState<Record<string, CuentaBancoCordobas>>({});
  const cuentaVista = (x: EnCola): CuentaBancoCordobas => cuentaEnCordobas(config, cuentas[x.clave]);

  const total = pendientes.reduce((a, x) => a + x.monto, 0);
  const destino = destinoDe;

  /** The payment «No llegó» was tapped on, and whose account it could go on instead. */
  const enRechazo = rechazando ? pendientes.find((x) => x.clave === rechazando) : undefined;
  const clienteDelRechazo = enRechazo?.venta?.clienteId ? clientes.find((c) => c.id === enRechazo.venta!.clienteId) : undefined;

  async function rechazar(x: EnCola, aFiado: boolean) {
    await emitir('pago.rechazado', { ...destino(x), motivo: motivo.trim(), ...(aFiado ? { aFiado } : {}) });
    setRechazando(null);
    setMotivo('');
  }

  return (
    <Pagina
      titulo={t('transf.titulo')}
      volverA={desde === 'ventas' ? '/ventas' : '/'}
      subtitulo={
        pendientes.length > 0
          ? t('transf.porVerificar', { monto: formatMoney(total as never) })
          : undefined
      }
    >
      {pendientes.length === 0 ? (
        <Vacio mensaje={t('transf.ninguna')} icono="transferencias" />
      ) : (
        <ul className="flex flex-col gap-2">
          {pendientes.map((x) => {
            const dias = diasDesde(x.fecha);
            const referencia = x.referencia ?? refs[x.clave]?.trim() ?? '';
            const repetida = otrosUsos(usos, referencia, x.clave)[0];
            const clienteId = x.venta?.clienteId ?? x.abono?.clienteId;
            const cliente = clienteId ? clientes.find((c) => c.id === clienteId) : undefined;
            return (
              <li key={x.clave} className="tarjeta">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="tabular font-medium">
                      {x.venta
                        ? x.venta.ticket
                        : x.abono
                          ? t('transf.abono')
                          : t('transf.recarga', { operadora: x.recarga!.operadora, telefono: x.recarga!.telefono })}
                    </p>
                    <p className="mt-0.5 text-xs text-slate-500">
                      {x.fecha} · {t(x.forma === 'billetera' ? 'pago.billetera' : 'pago.transferencia')}
                      {x.referencia && ` · ${t('transf.referencia', { ref: x.referencia })}`}
                      {cliente && ` · ${cliente.nombre}`}
                    </p>
                    <p className={`mt-0.5 text-xs ${dias >= 2 ? 'text-rose-300' : dias === 1 ? 'text-amber-300' : 'text-slate-500'}`}>
                      {dias === 0 ? t('transf.deHoy') : dias === 1 ? t('transf.deAyer') : t('transf.haceDias', { n: dias })}
                    </p>
                    {repetida && (
                      <p className="mt-1 text-xs font-medium text-rose-300">
                        {t(yaPago(repetida, true).clave, yaPago(repetida, true).valores)}
                      </p>
                    )}
                  </div>
                  <span className="tabular shrink-0 font-semibold">{formatMoney(x.monto)}</span>
                </div>

                {puede && (
                  <div className="mt-3 border-t border-slate-800 pt-3">
                    <div className="flex flex-col gap-3">
                      {!x.referencia && (
                        <Campo
                          maxLength={80}
                          etiqueta={t('transf.referenciaAlConfirmar')}
                          value={refs[x.clave] ?? ''}
                          onChange={(e) => setRefs((r) => ({ ...r, [x.clave]: e.target.value }))}
                          autoComplete="off"
                        />
                      )}
                      {x.forma !== 'billetera' && (
                        <ElegirCuenta
                          valor={cuentaVista(x)}
                          onCambiar={(c) => setCuentas((v) => ({ ...v, [x.clave]: c as CuentaBancoCordobas }))}
                          etiqueta={t('transf.enQueCuenta')}
                        />
                      )}
                      <div className="flex gap-3">
                        <button
                          type="button"
                          className="btn-secundario h-11 min-h-0 flex-1 text-sm"
                          onClick={() => {
                            setRechazando(x.clave);
                            setMotivo('');
                          }}
                        >
                          <Ico icono="aviso" className="size-4" />
                          {t('transf.noLlego')}
                        </button>
                        <button
                          type="button"
                          className="btn-primario h-11 min-h-0 flex-1 text-sm"
                          disabled={!referencia}
                          onClick={() =>
                            void emitir('pago.confirmado', {
                              ...destino(x),
                              referencia,
                              ...(x.forma === 'billetera' ? {} : { cuenta: cuentaVista(x) }),
                            })
                          }
                        >
                          <Ico icono="confirmar" className="size-4" />
                          {t('transf.confirmar')}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* A sheet, not a form that opened in the card and pushed the queue down (the owner, 2026-10-06). */}
      {enRechazo && (
        <Modal
          titulo={t('transf.noLlegoTitulo')}
          onCerrar={() => setRechazando(null)}
          pie={
            <div className="flex flex-col gap-3">
              {clienteDelRechazo && (
                <button
                  type="button"
                  className="btn-secundario w-full"
                  disabled={!motivo.trim()}
                  onClick={() => void rechazar(enRechazo, true)}
                >
                  <Ico icono="fiado" />
                  {t('transf.pasarAFiado', { nombre: clienteDelRechazo.nombre })}
                </button>
              )}
              <button
                type="button"
                className="btn-primario w-full"
                disabled={!motivo.trim()}
                onClick={() => void rechazar(enRechazo, false)}
              >
                <Ico icono="desactivar" />
                {t('transf.registrarPerdida')}
              </button>
            </div>
          }
        >
          <div className="flex flex-col gap-4" data-transferencia-rechazo>
            <p className="text-sm text-slate-400">
              {enRechazo.venta
                ? enRechazo.venta.ticket
                : t('transf.recarga', { operadora: enRechazo.recarga!.operadora, telefono: enRechazo.recarga!.telefono })}{' '}
              · {enRechazo.fecha} · <span className="tabular">{formatMoney(enRechazo.monto)}</span>
              {enRechazo.referencia && ` · ${t('transf.referencia', { ref: enRechazo.referencia })}`}
            </p>
            <Campo
              maxLength={200}
              etiqueta={t('transf.quePaso')}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder={t('transf.ejemplo')}
              autoFocus
              autoComplete="off"
            />
          </div>
        </Modal>
      )}
    </Pagina>
  );
}
